<template>
  <CCard>
    <CCardHeader class="font-weight-bolder"> {{ $t('settings.twoWay.numbersList.header') }} </CCardHeader>
    <CCardBody>
      <SenditTable
        pagination
        :headers="tableHeaders"
        :registers="processedRegisters"
        :itemsPerPage="itemsPerPage"
        :currentPage="currentPage"
        :totalPages="totalPages"
        :loading="loading"
        @updatePage="handlerChangePage"
      >
        <template #createdDateTime="{ item }">
          <td>
            {{ formatDate(item.createdDateTime) }}
          </td>
        </template>

        <template #beginDatetime="{ item }">
          <td>
            {{ formatDateTime(item.beginDatetime) }}
          </td>
        </template>

        <template #endDatetime="{ item }">
          <td>
            {{ formatDateTime(item.endDatetime) }}
          </td>
        </template>

        <template #status="{ item }">
          <td class="text-uppercase font-weight-bolder" :class="`text-${handlerStatus(item.beginDatetime, item.endDatetime).color}`">
            {{ $t(handlerStatus(item.beginDatetime, item.endDatetime).text) }}
          </td>
        </template>

        <template #actions="{ item }">
          <td class="text-center">
            <span style="cursor: pointer" @click="redirectToEdit(item.id)">
              <CIcon
                v-c-tooltip="{
                  content: $t('settings.twoWay.numbersList.tooltip.edit'),
                  placement: 'botton',
                }"
                name="cilPencil"
                size="custom-size"
                :height="22"
                viewBox="0 0 32 32"
              />
            </span>
            <span style="cursor: pointer" @click="redirectToDetails(item.number)">
              <CIcon
                v-c-tooltip="{
                  content: $t('settings.twoWay.numbersList.tooltip.results'),
                  placement: 'botton',
                }"
                name="cilChevronRight"
                size="custom-size"
                class="ml-3"
                :height="24"
                viewBox="0 0 32 32"
              />
            </span>
          </td>
        </template>
      </SenditTable>
    </CCardBody>
  </CCard>
</template>

<script>
import SenditTable from '@/components/table/SenditTable.vue'
import services from '@/services.js'
import { dateFormatString, dateTimeShortFormatString } from '@/utils/dateUtil'
import { DateTime } from 'luxon'

// {
//   "accessNumberId": "4d1173e4-5cde-4c6a-9c5b-15713dd05855",
//   "number": "4444444",
//   "countryId": "PT",
//   "organizationId": "e2743dd6-0ac5-4251-8c12-3646f53adcf7",
//   "timezone": "Europe/Lisbon",
//   "timezoneOffset": 0,
//   "closeProcessedDatetime": null,
//   "gracePeriodInDays": null,
//   "beginDatetime": "2024-11-21T15:30:44.23",
//   "endDatetime": null,
//   "keywordCount": 1,
//   "keywords": [
//     {
//       "organizationAccessNumberId": "2827db63-9aec-424c-9d0a-4545c99b761c",
//       "name": null,
//       "isWildcard": true,
//       "isDefault": true,
//       "webhooksUrl": null,
//       "keywordBehaviours": [
//         1
//       ],
//       "keywordStatus": 1,
//       "createdByUserId": "00000000-0000-0000-0000-000000000000",
//       "automaticResponses": [],
//       "id": "7a11bbd5-78b3-484a-a65b-ddcd16e036a0",
//       "createdDateTime": "2024-11-18T15:38:20.441213",
//       "updatedDateTime": null,
//       "createdBy": "System Administrator",
//       "lastUpdatedBy": null
//     }
//   ],
//   "id": "2827db63-9aec-424c-9d0a-4545c99b761c",
//   "createdDateTime": "2024-11-18T15:38:20.441213",
//   "updatedDateTime": null,
//   "createdBy": "Maria Oliveiraaa",
//   "lastUpdatedBy": null
// }

export default {
  name: 'ConfigurationListNumbers',
  components: {
    SenditTable,
  },
  data() {
    return {
      registers: [],
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 0,
      filters: {
        Page: 0,
        Count: 10,
      },
      loading: false,
    }
  },
  mounted() {
    this.getAccessNumbers()
  },
  methods: {
    getAccessNumbers() {
      const organizationId = this.$route.params.organizationId

      this.filters = {
        Page: this.currentPage - 1,
        Count: this.itemsPerPage,
      }

      this.loading = true

      services
        .getTwoWayAccessNumbers(organizationId, this.filters)
        .then((res) => {
          this.registers = res.data.data.organizationAccessNumbers
          this.currentPage = res.data.data.pagination.page + 1
          this.totalPages = Math.ceil(res.data.data.totalRecords / this.itemsPerPage)
        })
        .finally(() => (this.loading = false))
    },
    handlerChangePage(currentPage) {
      this.currentPage = currentPage
      this.getAccessNumbers()
    },

    formatDate(date) {
      return date ? dateFormatString(date) : ''
    },

    formatDateTime(date) {
      return date ? dateTimeShortFormatString(date).replace(',', '') : ''
    },

    redirectToEdit(accessNumberId) {
      this.$router.push({ name: 'edit-two-way-configuration', params: { configurationId: accessNumberId } })
    },

    redirectToDetails(accessNumber /*, iniDate, finalDate */) {
      this.$router.push({ name: 'twoway-results', query: { accessNumber: accessNumber } })
    },

    handlerStatus(dateIni, dateFim) {
      const currentDate = DateTime.fromJSDate(new Date())
      const d1 = DateTime.fromISO(dateIni)
      const d2 = DateTime.fromISO(dateFim)
      if (currentDate > d1) {
        if (currentDate > d2)
          return {
            text: 'settings.twoWay.numbersList.table.status.inactive',
            color: 'error',
          }
        else {
          return {
            text: 'settings.twoWay.numbersList.table.status.active',
            color: 'success',
          }
        }
      } else {
        return {
          text: 'settings.twoWay.numbersList.table.status.pending',
          color: 'warning',
        }
      }
    },
  },
  computed: {
    tableHeaders() {
      return [
        { key: 'number', label: this.$t('settings.twoWay.numbersList.table.columns.destinationNumber'), sorter: true, _classes: 'text-center' },
        { key: 'createdDateTime', label: this.$t('settings.twoWay.numbersList.table.columns.createdDate'), sorter: false, _classes: 'text-center' },
        { key: 'beginDatetime', label: this.$t('settings.twoWay.numbersList.table.columns.beginDate'), sorter: false, _classes: 'text-center' },
        { key: 'endDatetime', label: this.$t('settings.twoWay.numbersList.table.columns.endDate'), sorter: false, _classes: 'text-center' },
        { key: 'status', label: this.$t('settings.twoWay.numbersList.table.columns.status'), sorter: false, _classes: 'text-center' },
        { key: 'keywordCount', label: this.$t('settings.twoWay.numbersList.table.columns.keywordsNumber'), sorter: false, _classes: 'text-center' },
        { key: 'actions', label: this.$t('settings.twoWay.numbersList.table.columns.actions'), sorter: false, _classes: 'text-center' },
      ]
    },
    processedRegisters() {
      return this.registers.map((el) => {
        return {
          ...el,
          _classes: 'text-center',
        }
      })
    },
  },
}
</script>
